import { AlertTriangle } from "lucide-react";
import { type JSX } from "react";

import { maintenanceMessages } from "../maintenance.intl";
import type { MaintenanceHistoryEntry } from "../types/maintenance.types";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import {
  dateFormat,
  DAY_MONTH_YEAR_TIME_FORMAT,
} from "@/utils/datetime/dateTimeHelper";

interface MaintenanceNotificationBannerProps {
  schedule: MaintenanceHistoryEntry;
}

/**
 * Renders the non-blocking pre-maintenance notification banner.
 *
 * @param props Published maintenance schedule.
 * @returns Maintenance notification banner.
 */
const MaintenanceNotificationBanner = (
  props: MaintenanceNotificationBannerProps,
): JSX.Element => {
  const { schedule } = props;
  const t = useIntlMessages(maintenanceMessages);
  const startDateLabel = dateFormat(
    schedule.startAt,
    DAY_MONTH_YEAR_TIME_FORMAT,
  );

  const remainingDuration = Math.round(
    (new Date(schedule.startAt).getTime() - new Date().getTime()) / 3_600_000,
  );
  const durationLabel = remainingDuration
    ? `${Math.round(remainingDuration)}h`
    : undefined;

  return (
    <div className='fixed inset-x-0 top-0 z-[60] flex min-h-10 items-center justify-center border-b border-orange-200 bg-orange-50 px-4 py-2 text-center text-sm text-orange-800'>
      <div className='flex items-center gap-2'>
        <span className='flex size-5 items-center justify-center rounded-full bg-orange-100 text-orange-600'>
          <AlertTriangle size={14} />
        </span>
        <span>
          <strong>{t("notificationTitle")}</strong> {t("willBeginOn")}{" "}
          <strong>{startDateLabel}</strong>{" "}
          {durationLabel && `(${durationLabel} from now)`}
        </span>
      </div>
    </div>
  );
};

export default MaintenanceNotificationBanner;
